import { Injectable } from '@angular/core';
import { map, Observable, switchMap } from 'rxjs';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';

import { IProfileLink } from '@shared/models/links';
import { FeatureFlagsService } from '@core/services/feature-flags.service';

import { Store } from '@ngrx/store';
import { AppState } from '@state/app.state';
import { selectAuthUser } from '@state/auth/auth.selectors';
import { selectOrganizationUrlSlug } from '@state/organization/organization.selectors';

@UntilDestroy()
@Injectable({ providedIn: 'root' })
export class ProfileLinksService {
  constructor(
    private store: Store<AppState>,
    private featureFlagsService: FeatureFlagsService
  ) {}

  getProfileLinks(): Observable<IProfileLink[]> {
    return this.featureFlagsService.isEnabled('fe_sell_features').pipe(
      switchMap((sellFeatureFlag) =>
        this.store
          .select(selectOrganizationUrlSlug)
          .pipe(untilDestroyed(this))
          .pipe(
            map((slug) => {
              const marketplacePath = slug ? `/o/${slug}` : '/';
              return {
                marketplacePath,
                slug,
              };
            }),
            // Combine with the user organization
            switchMap(({ marketplacePath, slug }) => {
              return this.store.select(selectAuthUser).pipe(
                map(
                  (user) => {
                    const userOrg = sellFeatureFlag ? user?.organization : null;
                    const externalOrg =
                      sellFeatureFlag &&
                      slug &&
                      userOrg?.shortName?.toLowerCase() !== slug;

                    // const userOrg = user?.organization;
                    // const externalOrg =
                    //   slug && userOrg?.shortName?.toLowerCase() !== slug;

                    return [
                      {
                        title: 'Buy',
                        path: null,
                        icon: 'featherHome',
                        children: [
                          {
                            title:
                              userOrg?.shortName && !externalOrg
                                ? 'My Marketplace'
                                : 'Marketplace',
                            path:
                              userOrg?.shortName && !externalOrg
                                ? `/o/${userOrg.shortName.toLowerCase()}`
                                : marketplacePath,
                            pathsToCheck: ['/page/', '/page/1', '/page/1/'],
                          },
                          { title: 'Offers', path: '/profile/offers' },
                          {
                            title: 'Favorites',
                            path: '/profile/favorites',
                            featureFlag: 'fe_favorite_property',
                          },
                          {
                            title: 'Buy Boxes',
                            path: '/buy-boxes',
                            featureFlag: 'fe_buy_boxes',
                          },
                        ],
                      },
                      {
                        title: 'Sell',
                        path: null,
                        icon: 'featherDollarSign',
                        featureFlag: 'fe_sell_features',
                        children: [
                          {
                            title: 'My Deals',
                            path: '/my-deals',
                          },
                          {
                            title: 'Upload Buyer List',
                            path: '/upload-buyers',
                          },
                        ],
                      },
                      {
                        title: 'Account',
                        path: null,
                        icon: 'featherUser',
                        children: [
                          {
                            title: 'My Organization',
                            featureFlag: 'fe_sell_features',
                            path: '/my-organization',
                          },
                          {
                            title: 'Profile',
                            path: '/profile/account',
                          },
                          {
                            title: 'Logout',
                            path: '/logout',
                          },
                        ],
                      },
                    ];
                  }
                  // {
                  //   title: 'UI Docs',
                  //   path: null,
                  //   icon: 'featherFileText',
                  //   featureFlag: 'fe_ui_docs',
                  //   removeFromProduction: true,
                  //   children: [
                  //     {
                  //       title: 'Button',
                  //       path: '/ui-docs/button',
                  //     },
                  //     {
                  //       title: 'Colors',
                  //       path: '/ui-docs/colors',
                  //     },
                  //     {
                  //       title: 'Multi Selector',
                  //       path: '/ui-docs/multi-selector',
                  //     },
                  //   ],
                  // },
                )
              );
            })
          )
      )
    );
  }
}
